<template>
  <div data-qa="doctor-appointment-restrictions">
    <CoreDialog
      v-model="isLoadingDialogOpen"
      data-qa="doctor-appointment-restrictions-loading"
      size="sm"
    >
      <div class="d-flex flex-column align-center justify-center py-10">
        <v-progress-circular
          width="2.5"
          size="42"
          color="primary"
          indeterminate
        />
      </div>
    </CoreDialog>

    <component
      :is="isMobile ? CoreBottomSheet : CoreDialog"
      v-model="isContentDialogOpen"
      data-qa="doctor-appointment-restrictions-modal"
      v-bind="isMobile
        ? {}
        : {
          size: 'md',
          title,
          divider: true,
          'content-class': 'doctor-appointment-restrictions-modal',
        }"
    >
      <div
        v-if="isMobile"
        class="text-h6 text--text pt-2 pb-4"
      >
        {{ title }}
      </div>

      <div class="d-flex flex-column overflow-y-hidden">
        <div
          v-if="lpuName"
          class="d-flex align-center py-1 mb-4"
        >
          <v-icon
            class="mr-4"
            color="icon-secondary"
          >
            ui-icon-clinic
          </v-icon>
          <span class="text-body-1 text-break text--text">{{ lpuName }}</span>
        </div>
        <div class="custom-scroll overflow-y-auto text--text">
          <div
            v-for="(group, index) in restrictionsGroups"
            :key="`${group.title}-${index}`"
            :class="{ 'mt-4': index !== 0 }"
          >
            <div class="text-subtitle-1 text-break mb-1">
              {{ group.title }}
            </div>
            <ul>
              <li
                v-for="(restriction, restrictionIndex) in group.restrictions"
                :key="restrictionIndex"
                class="text-body-1 text-break"
              >
                {{ restriction }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <template #actions>
        <v-btn
          data-qa="modal-actions-btn-close"
          color="primary"
          class="branding-btn branding-btn--primary"
          block
          depressed
          @click="isContentDialogOpen = false"
        >
          {{ t('Понятно', l10nOptions) }}
        </v-btn>
      </template>
    </component>

    <CoreDialog
      v-model="isErrorDialogOpen"
      data-qa="doctor-appointment-restrictions-error"
      size="sm"
      :title="errorTitle"
      divider
    >
      <div class="text-body-1 text-secondary--text">
        {{ errorText }}
      </div>
      <template #actions>
        <v-btn
          color="primary"
          class="branding-btn branding-btn--primary"
          block
          depressed
          @click="reloadPage"
        >
          {{ t('Обновить', l10nOptions) }}
        </v-btn>
      </template>
    </CoreDialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import i18next from 'i18next'
import useIsMobile from 'src/composables/useIsMobile'
import CoreBottomSheet from 'src/core/CoreBottomSheet/CoreBottomSheet.vue'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import apiDoctorAppointmentRestrictions from 'src/components/common/DoctorAppointmentRestrictionsModal/api/apiDoctorAppointmentRestrictions'
import mapDoctorAppointmentRestrictions from 'src/components/common/DoctorAppointmentRestrictionsModal/functions/mapDoctorAppointmentRestrictions'
import type { DoctorAppointmentRestrictionsGroup } from 'src/components/common/DoctorAppointmentRestrictionsModal/types'

type Emits = {
  (_event: 'input', _value: boolean): void
}

const props = defineProps<{
  value: boolean
  doctorId: number
  lpuId: number
  lpuName: string
}>()
const emit = defineEmits<Emits>()

const { t, language } = i18next
const l10nOptions = { ns: language }
const title = computed(() => t('Ограничения на приём к врачу', l10nOptions))
const isMobile = useIsMobile()
const isLoadingDialogOpen = ref(false)
const isContentDialogOpen = ref(false)
const isErrorDialogOpen = ref(false)
const restrictionsGroups = ref<DoctorAppointmentRestrictionsGroup[]>([])
const errorTitle = ref('')
const errorText = ref('')
const responseCache = new Map<string, DoctorAppointmentRestrictionsGroup[]>()

function isNetworkDisconnect(error: unknown) {
  if (!process.env.IS_CLIENT) {
    return false
  }

  const axiosError = error as { message?: string, code?: string }

  return axiosError?.message === 'Network Error'
    || axiosError?.code === 'ERR_NETWORK'
    || !window.navigator.onLine
}

function reloadPage() {
  if (process.env.IS_CLIENT) {
    window.location.reload()
  }
}

async function waitMinDelay(start: number) {
  const remainingDelay = 500 - Math.trunc(performance.now() - start)

  if (remainingDelay > 0) {
    await new Promise(resolve => {
      setTimeout(resolve, remainingDelay)
    })
  }
}

async function loadRestrictions() {
  const cacheKey = `${props.doctorId}:${props.lpuId}`
  const start = performance.now()

  isErrorDialogOpen.value = false
  isContentDialogOpen.value = false
  isLoadingDialogOpen.value = true

  try {
    if (!responseCache.has(cacheKey)) {
      const data = await apiDoctorAppointmentRestrictions.get({
        doctor_id: props.doctorId,
        lpu_id: props.lpuId,
      })
      const mapped = mapDoctorAppointmentRestrictions(data)

      responseCache.set(cacheKey, mapped)
      await waitMinDelay(start)
    }

    const groups = responseCache.get(cacheKey) || []

    if (!groups.length) {
      throw new Error('Empty list')
    }

    restrictionsGroups.value = groups

    if (!isLoadingDialogOpen.value) {
      return
    }

    isContentDialogOpen.value = true
    isLoadingDialogOpen.value = false
  } catch (error) {
    responseCache.delete(cacheKey)
    await waitMinDelay(start)

    if (!isLoadingDialogOpen.value) {
      return
    }

    if (isNetworkDisconnect(error)) {
      errorTitle.value = t('Нет интернета', l10nOptions)
      errorText.value = t('Проверьте соединение и обновите страницу', l10nOptions)
    } else {
      errorTitle.value = t('Что-то пошло не так', l10nOptions)
      errorText.value = t('Попробуйте немного подождать и обновите страницу', l10nOptions)
    }

    isErrorDialogOpen.value = true
    isLoadingDialogOpen.value = false
  }
}

watch(() => props.value, isOpen => {
  if (isOpen) {
    loadRestrictions()

    return
  }

  isLoadingDialogOpen.value = false
  isContentDialogOpen.value = false
  isErrorDialogOpen.value = false
})

watch(isContentDialogOpen, isOpen => {
  if (!isOpen && props.value && !isLoadingDialogOpen.value && !isErrorDialogOpen.value) {
    emit('input', false)
  }
})

watch(isLoadingDialogOpen, isOpen => {
  if (!isOpen && props.value && !isContentDialogOpen.value && !isErrorDialogOpen.value) {
    emit('input', false)
  }
})

watch(isErrorDialogOpen, isOpen => {
  if (!isOpen && props.value && !isLoadingDialogOpen.value && !isContentDialogOpen.value) {
    emit('input', false)
  }
})
</script>

<style lang="scss">
.doctor-appointment-restrictions-modal [data-qa="core-dialog-content"] > * {
  overflow-y: auto;
}
</style>

<style lang="scss" scoped>
@import 'src/styles/mixins';

.custom-scroll {
  @include custom-scroll;
}
</style>
